// Auth context. One tiny React context backed by sessionStorage.
// ADR 0021: dev-auth JWT in sessionStorage; a closed tab logs the
// user out. Slice 38 (ADR 0034) adds the Supabase magic-link path:
// the LoginPage calls supabase.auth.signInWithOtp directly and the
// /auth/callback route calls `completeSupabaseSignIn` here to stash
// the resulting JWT. The two paths share the same storage shape so
// apiFetch and every downstream query are unchanged.

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { JSX, ReactNode } from 'react';
import { TOKEN_STORAGE_KEY, apiFetch, onUnauthorized } from '../api/client.js';
import type { LoginResponse } from '../api/types.js';

interface AuthUser {
  readonly id: string;
  readonly email: string;
}

interface AuthState {
  readonly token: string | null;
  readonly user: AuthUser | null;
}

interface AuthContextValue extends AuthState {
  readonly login: (email: string) => Promise<void>;
  // Slice 38 — the Supabase callback route calls this with the
  // access_token Supabase handed back + optionally the email claim.
  readonly completeSupabaseSignIn: (token: string, email: string | null) => void;
  readonly logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const USER_STORAGE_KEY = 'tabletop.user';

function readPersisted(): AuthState {
  try {
    const token = sessionStorage.getItem(TOKEN_STORAGE_KEY);
    const userRaw = sessionStorage.getItem(USER_STORAGE_KEY);
    const user = userRaw ? (JSON.parse(userRaw) as AuthUser) : null;
    return { token, user: token ? user : null };
  } catch {
    return { token: null, user: null };
  }
}

function persist(state: AuthState): void {
  try {
    if (state.token) sessionStorage.setItem(TOKEN_STORAGE_KEY, state.token);
    else sessionStorage.removeItem(TOKEN_STORAGE_KEY);
    if (state.user) sessionStorage.setItem(USER_STORAGE_KEY, JSON.stringify(state.user));
    else sessionStorage.removeItem(USER_STORAGE_KEY);
  } catch {
    /* storage disabled: keep the in-memory copy only */
  }
}

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [state, setState] = useState<AuthState>(() => readPersisted());

  const logout = useCallback(() => {
    const next: AuthState = { token: null, user: null };
    persist(next);
    setState(next);
  }, []);

  const login = useCallback(async (email: string) => {
    const res = await apiFetch<LoginResponse>('/dev/login', {
      method: 'POST',
      body: { email },
      anonymous: true,
    });
    const next: AuthState = { token: res.token, user: res.user };
    persist(next);
    setState(next);
  }, []);

  const completeSupabaseSignIn = useCallback((token: string, email: string | null) => {
    // The Supabase callback hands back a JWT and (usually) the email
    // claim. We don't know the local users.id yet — the first /v1/me
    // call under the new session will trigger reconciliation on the
    // server, and HomePage reads memberships from there. Keep `id`
    // empty-string until that query resolves; HomePage never reads
    // `user.id`, only the server response.
    const next: AuthState = { token, user: { id: '', email: email ?? '' } };
    persist(next);
    setState(next);
  }, []);

  // A 401 from anywhere in the app bounces us back to logged-out.
  useEffect(() => onUnauthorized(logout), [logout]);

  const value = useMemo<AuthContextValue>(
    () => ({ ...state, login, completeSupabaseSignIn, logout }),
    [state, login, completeSupabaseSignIn, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
